// Importing necessary types
import {
  CreateDepartmentResponse,
  ICreateDepartmentPayload,
  IEditDepartmentResponse,
  IUserDetailsCommon,
  Nullable,
} from "../../../Types/Common";
import ApiService from "../../ApiService";
import { IDepartmentTaskListServiceResponse } from "../../../features/dailyTaskList/types";

enum DepartmentEndpoints {
  DEPARTMENT_LIST = "/department-list",
  ADD_DEPARTMENT = "/department/store",
  UPDATE_DEPARTMENT = "/department/update",
  CREATE_DEPARTMENT_TASKS = "/department-tasks/add-update",
  DEPARTMENT_CATEGORY_LIST = "/department-tasks/list",
  DELETE_DEPARTMENT_CATEGORY_LIST = "/department-tasks/delete",
}

// Define the DepartmentService class that extends the ApiService class to interact with department-related APIs
class DepartmentService extends ApiService {
  // Method to fetch all departments
  async getAllDepartment(
    status: string = "active"
  ): Promise<{ data: IUserDetailsCommon[] }> {
    return this.get<{ data: IUserDetailsCommon[] }>(
      `${DepartmentEndpoints.DEPARTMENT_LIST}?status=${status}`
    );
  }

  // Method to fetch sub-departments based on the department ID
  async getSubDepartments(id: number): Promise<{ data: IUserDetailsCommon[] }> {
    return this.get<{ data: IUserDetailsCommon[] }>(
      DepartmentEndpoints.DEPARTMENT_LIST + "/" + id
    );
  }

  async createDepartment(
    payload: ICreateDepartmentPayload
  ): Promise<CreateDepartmentResponse> {
    return this.post<CreateDepartmentResponse>(
      DepartmentEndpoints.ADD_DEPARTMENT,
      payload
    );
  }

  async getEditDepartment(id: number): Promise<IEditDepartmentResponse> {
    return this.get(`/department/edit/${id}`);
  }

  async updateDepartment(
    id: number,
    payload: ICreateDepartmentPayload
  ): Promise<CreateDepartmentResponse> {
    return this.post<CreateDepartmentResponse>(
      `${DepartmentEndpoints.UPDATE_DEPARTMENT}/${id}`,
      payload
    );
  }

  async updateDepartmentStatus(data: {
    id: number;
    status: string;
  }): Promise<{ status: boolean; message: string }> {
    return this.post(`/department/status/update/${data.id}`, {
      status: data.status,
    });
  }

  async getDepartmentsWiseCategoryList(params: {
    department_id: number;
    node_level?: number;
    size?: number;
    page?: number;
    parent_id?: number;
  }): Promise<IDepartmentTaskListServiceResponse> {
    const queryParams = new URLSearchParams();
    if (params.department_id)
      queryParams.append("department_id", params.department_id.toString());
    if (params?.node_level)
      queryParams.append("node_level", params?.node_level.toString());
    if (params?.page) queryParams.append("page", params?.page?.toString());
    if (params?.size) queryParams.append("size", params?.size?.toString());
    if (params?.parent_id)
      queryParams?.append("parent_id", params?.parent_id?.toString());
    const queryString = queryParams.toString();

    return this.get(
      `${DepartmentEndpoints.DEPARTMENT_CATEGORY_LIST}${
        queryString ? `?${queryString}` : ""
      }`
    );
  }

  async createDepartmentTasks({
    id,
    name,
    is_active,
    parent_id,
    node_level,
    description,
    department_id,
  }: {
    department_id: number;
    name: string;
    parent_id: Nullable<number>;
    node_level: number;
    description: string;
    is_active: boolean;
    id: Nullable<number>;
  }) {
    const payload = {
      id,
      name,
      is_active,
      parent_id,
      node_level,
      description,
      department_id,
    };
    return this.post(DepartmentEndpoints.CREATE_DEPARTMENT_TASKS, payload);
  }
  async deleteDepartmentCategory({
    id,
  }: {
    id: number;
  }): Promise<{ status: boolean; message: string }> {
    return this.delete<Promise<{ status: boolean; message: string }>>(
      DepartmentEndpoints.DELETE_DEPARTMENT_CATEGORY_LIST,
      {
        id,
      }
    );
  }
}

// Creating an instance of DepartmentService and exporting it for use in other parts of the application
export const departmentService = new DepartmentService();
